/**
 * Consent-gated dataLayer pushes.
 *
 * GTM blocks tags that fire before the consent management platform has applied a
 * consent state, and it does not replay them once consent arrives. This
 * application injects GTM itself (see googleAnalytics.ts) and the HTML template
 * carries no consent-default snippet, so the CMP always loads behind GTM and the
 * page-entry events lose that race on every page load.
 *
 * This module holds tracking pushes back until a consent state exists, then
 * releases them. Pushes marked as page-entry events are re-pushed once if the
 * visitor grants analytics consent later in the same page view.
 *
 * It is deliberately CMP-agnostic. The only signal it watches is the Google
 * Consent Mode call, which any CMP has to make for GTM to work at all, so
 * replacing the CMP does not require touching this file.
 */

type DataLayerEntry = Record<string, unknown>;
type ConsentState = 'granted' | 'denied';

export type TrackingPushOptions = {
  /**
   * Re-push this event once if analytics consent is granted later in the same
   * page view. Only the page-entry events (`/online/start`) set this: the form,
   * campaign and confirmation events reuse the same event names and would be
   * double-counted if they were replayed too.
   */
  replayOnLateConsent?: boolean;
};

const SCAN_INTERVAL_MS = 50;
const FAIL_OPEN_MS = 5000; // release the buffer even if no consent signal arrives
const OBSERVE_WINDOW_MS = 60000; // keep watching for a late grant

/**
 * Entries pushed as one unit, so a replayed event keeps the field-clearing push
 * that belongs in front of it.
 */
type PushGroup = {
  entries: DataLayerEntry[];
  replayOnLateConsent: boolean;
};

let observing = false;
let consentSeen = false;
let analyticsGranted = false;
let replayed = false;
let timer: number | undefined;

let buffer: PushGroup[] = [];
let replayable: PushGroup[] = [];

function getDataLayer(): any[] {
  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  return w.dataLayer;
}

function rawPush(group: PushGroup): void {
  const dataLayer = getDataLayer();
  group.entries.forEach((entry) => dataLayer.push(entry));
}

/** Reads analytics_storage out of either form of Consent Mode signal. */
function readAnalyticsConsent(entry: any): ConsentState | null {
  if (!entry) {
    return null;
  }

  // Page-level call: gtag("consent", "default" | "update", { ... })
  if (entry[0] === 'consent' && (entry[1] === 'default' || entry[1] === 'update')) {
    const state = entry[2];
    if (state && (state.analytics_storage === 'granted' || state.analytics_storage === 'denied')) {
      return state.analytics_storage;
    }
  }

  // Container-side signal, as emitted by our GTM containers
  const value = entry.value || entry;
  if (value && (value.event === 'gtm_consent_default' || value.event === 'gtm_consent_update')) {
    if (value.analytics_storage === 'granted' || value.analytics_storage === 'denied') {
      return value.analytics_storage;
    }
  }

  return null;
}

function onConsentSignal(state: ConsentState): void {
  const grantedNow = state === 'granted';

  if (!consentSeen) {
    consentSeen = true;
    analyticsGranted = grantedNow;

    while (buffer.length) {
      rawPush(buffer.shift() as PushGroup);
    }
    return;
  }

  // Consent changed later in the same page view. Replay the page-entry events
  // once, but only if they were released while analytics consent was denied.
  if (!analyticsGranted && grantedNow && !replayed) {
    replayed = true;
    analyticsGranted = true;
    replayable.forEach(rawPush);
  }
}

function stopObserving(): void {
  if (timer !== undefined) {
    window.clearInterval(timer);
    timer = undefined;
  }
}

/**
 * Scan dataLayer for consent signals.
 *
 * We poll the array rather than wrapping dataLayer.push, because GTM replaces
 * dataLayer.push with its own implementation when it initialises and would drop
 * our wrapper depending on load order. GTM's implementation still appends to the
 * same array, so scanning it is reliable regardless of who owns .push.
 *
 * Safe to call more than once: the observer is started at most once per page
 * view. It is started explicitly on mount and defensively on the first push, so
 * a push can never be buffered without something watching for its release.
 */
export function startConsentObserver(): void {
  if (observing) {
    return;
  }
  observing = true;

  let scanned = 0;
  let waited = 0;

  timer = window.setInterval(() => {
    const dataLayer = getDataLayer();
    while (scanned < dataLayer.length) {
      const state = readAnalyticsConsent(dataLayer[scanned++]);
      if (state) {
        onConsentSignal(state);
      }
    }

    waited += SCAN_INTERVAL_MS;

    // Fail open: never suppress events indefinitely because a CMP failed to
    // load. GTM still blocks the tags if consent is missing, so nothing leaks.
    if (!consentSeen && waited >= FAIL_OPEN_MS) {
      onConsentSignal('denied');
    }

    if (analyticsGranted || waited >= OBSERVE_WINDOW_MS) {
      stopObserving();
    }
  }, SCAN_INTERVAL_MS);
}

/**
 * The only function the application calls. Entries are pushed to dataLayer as a
 * unit, once a consent state has been applied for this page view.
 */
export function pushTrackingEvent(
  entries: DataLayerEntry[],
  options: TrackingPushOptions = {}
): void {
  startConsentObserver();

  const group: PushGroup = {
    entries,
    replayOnLateConsent: options.replayOnLateConsent === true,
  };

  // Already granted means the event reaches GA4 on its first push, so there is
  // nothing to replay.
  if (group.replayOnLateConsent && !analyticsGranted) {
    replayable.push(group);
  }

  if (consentSeen) {
    rawPush(group);
    return;
  }

  buffer.push(group);
}

/** Test-only. Resets the module state between test cases. */
export function resetConsentGate(): void {
  stopObserving();
  observing = false;
  consentSeen = false;
  analyticsGranted = false;
  replayed = false;
  buffer = [];
  replayable = [];
}
